import { useState, useEffect } from 'react';
import { Lock, ArrowLeft } from 'lucide-react';

interface Props {
  token: string;
  onNavigate: (page: string) => void;
}

export function ResetPasswordPage({ token, onNavigate }: Props) {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    fetch(`/api/v1/auth/password/validate_token?reset_password_token=${encodeURIComponent(token)}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then((r) => setTokenValid(r.ok))
      .catch(() => setTokenValid(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== passwordConfirmation) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/v1/auth/password', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reset_password_token: token,
          password,
          password_confirmation: passwordConfirmation,
        }),
      });

      if (res.ok) {
        setSuccess(true);
        setTimeout(() => onNavigate('login'), 3000);
        return;
      }

      const data = await res.json().catch(() => ({}));
      setError(data.error || 'No pudimos actualizar tu contraseña en este momento. Intentá nuevamente.');
    } catch {
      setError('No pudimos conectarnos en este momento. Revisá tu conexión e intentá otra vez.');
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-bg">
        <div className="text-zinc-500">Verificando enlace…</div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-app-bg p-4">
        <div className="w-full max-w-[24rem] flex flex-col items-center text-center">
          <h1 className="text-3xl font-bold text-zinc-900">Enlace inválido</h1>
          <p className="text-zinc-500 mt-2 mb-6">
            El enlace para restablecer tu contraseña ha expirado o es inválido.
          </p>
          <button
            type="button"
            onClick={() => onNavigate('forgot')}
            className="px-4 py-3 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 transition-colors"
          >
            Solicitar nuevo enlace
          </button>
          <button
            type="button"
            onClick={() => onNavigate('login')}
            className="mt-4 flex items-center gap-1.5 text-sm text-violet-600 hover:text-violet-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-app-bg p-4">
        <div className="w-full max-w-[24rem] flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-zinc-900">¡Contraseña actualizada!</h1>
          <p className="text-zinc-500 mt-2 mb-6">
            Tu contraseña fue actualizada correctamente. Serás redirigido al login en unos segundos.
          </p>
          <button
            type="button"
            onClick={() => onNavigate('login')}
            className="px-4 py-3 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 transition-colors"
          >
            Ir al login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-app-bg p-4">
      <div className="w-full max-w-[24rem] flex flex-col items-center">
        <h1 className="text-3xl font-bold text-zinc-900">Nueva contraseña</h1>
        <p className="text-zinc-500 mt-2 mb-6 text-center">Ingresa tu nueva contraseña.</p>

        {error && (
          <div className="w-full mb-4 px-4 py-3 rounded-lg bg-red-100 border border-red-500 text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-4">
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-zinc-400">
              <Lock className="h-5 w-5" />
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Nueva contraseña"
              required
              autoComplete="new-password"
              className="w-full py-3 pl-10 pr-4 mt-1 border border-zinc-300 rounded-3xl bg-white text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-zinc-400">
              <Lock className="h-5 w-5" />
            </span>
            <input
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              placeholder="Confirmar contraseña"
              required
              autoComplete="new-password"
              className="w-full py-3 pl-10 pr-4 mt-1 border border-zinc-300 rounded-3xl bg-white text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full mt-5 px-4 py-3 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Actualizando…' : 'Actualizar contraseña'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => onNavigate('login')}
          className="mt-6 flex items-center gap-1.5 text-sm text-violet-600 hover:text-violet-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al login
        </button>
      </div>
    </div>
  );
}
